import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save, FileText, User, MapPin, ArrowLeft } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { masterItems } from "../../data/masterItems"; // Step 1 wali file import kro
import { useParams, useNavigate } from "react-router-dom"; // Navigation ke liye

const CreateQuotation = () => {
  const { token } = useAdminAuth();
  const { id } = useParams(); // URL se ID lene ke liye (Edit Mode)
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // --- STATE ---
  const [client, setClient] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    siteAddress: "",
    date: new Date().toISOString().split('T')[0]
  });

  const [sections, setSections] = useState([
    {
      sectionName: "First Floor / Kitchen",
      items: [{ description: "", quantity: 1, rate: 0 }]
    }
  ]);

  const [adjustment, setAdjustment] = useState(0);

  // --- EDIT MODE: FETCH DATA ---
  useEffect(() => {
    if (id) {
      fetchQuotationData(id);
    }
  }, [id]);

  const fetchQuotationData = async (quoteId: string) => {
    try {
      const res = await fetch(`http://localhost:1001/api/quotations/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClient({
          clientName: data.clientName,
          clientEmail: data.clientEmail || "",
          clientPhone: data.clientPhone || "",
          siteAddress: data.siteAddress,
          date: data.date
        });
        setAdjustment(data.adjustment || 0);
        
        // Map Backend Data structure to Frontend State
        if (data.sections && data.sections.length > 0) {
            const mappedSections = data.sections.map((sec: any) => ({
                id: sec.id, // Keep ID for update
                sectionName: sec.sectionName,
                items: sec.items.map((item: any) => ({
                    id: item.id, // Keep ID for update
                    description: item.description,
                    quantity: item.quantity,
                    rate: item.rate
                }))
            }));
            setSections(mappedSections);
        }
      }
    } catch (err) {
      console.error("Error fetching data for edit", err);
      alert("Could not load quotation for editing");
    }
  };

  // --- HELPERS ---
  const handleClientChange = (e: any) => setClient({ ...client, [e.target.name]: e.target.value });

  const addSection = () => {
    setSections([...sections, { sectionName: "", items: [{ description: "", quantity: 1, rate: 0 }] }]);
  };

  const removeSection = (idx: number) => {
    const newSec = [...sections];
    newSec.splice(idx, 1);
    setSections(newSec);
  };

  const handleSectionNameChange = (idx: number, val: string) => {
    const newSec = [...sections];
    newSec[idx].sectionName = val;
    setSections(newSec);
  };

  // --- ITEM LOGIC ---
  const addItem = (sectionIdx: number) => {
    const newSec = [...sections];
    newSec[sectionIdx].items.push({ description: "", quantity: 1, rate: 0 });
    setSections(newSec);
  };

  const removeItem = (sectionIdx: number, itemIdx: number) => {
    const newSec = [...sections];
    newSec[sectionIdx].items.splice(itemIdx, 1);
    setSections(newSec);
  };

  const handleItemChange = (sIdx: number, iIdx: number, field: string, value: any) => {
    const newSec = [...sections];
    // @ts-ignore
    newSec[sIdx].items[iIdx][field] = value;
    setSections(newSec);
  };

  // --- NEW: DROPDOWN LOGIC ---
  const handleMasterItemSelect = (sIdx: number, iIdx: number, value: string) => {
    const newSec = [...sections];
    
    if (value === "custom") {
        // Do nothing, let user type
    } else {
        const selected = masterItems.find(m => m.description === value);
        if (selected) {
            newSec[sIdx].items[iIdx].description = selected.description;
            newSec[sIdx].items[iIdx].rate = selected.rate;
        }
    }
    setSections(newSec);
  };

  // --- CALCULATIONS ---
  const calculateTotal = () => {
    let total = 0;
    sections.forEach(sec => {
      sec.items.forEach(item => {
        total += (item.quantity * item.rate);
      });
    });
    return total;
  };

  // --- SAVE ---
  const handleSave = async () => {
    setLoading(true);
    const payload = {
      ...client,
      id: id ? parseInt(id) : null, // ID bhejo agar edit kar rahe ho
      totalAmount: calculateTotal(),
      adjustment: adjustment,
      finalTotal: calculateTotal() - adjustment,
      sections: sections
    };

    try {
      const res = await fetch("https://luxuriyahomes.com/api/quotations/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(id ? "Quotation Updated Successfully!" : "Quotation Saved Successfully!");
        if(!id) {
            // Reset logic only if new create
             setClient({ clientName: "", clientEmail: "", clientPhone: "", siteAddress: "", date: new Date().toISOString().split('T')[0] });
             setSections([{ sectionName: "First Floor / Kitchen", items: [{ description: "", quantity: 1, rate: 0 }] }]);
             setAdjustment(0);
        } else {
            navigate("/quotations"); // List page pe wapas jao
        }
      } else {
        alert("Failed to save.");
      }
    } catch (error) {
      console.error(error);
      alert("Error saving quotation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      {/* HEADER */}
      <div className="bg-[#161616] border-b border-zinc-800 p-6 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-4">
             {id && (
                 <button onClick={() => navigate("/quotations")} className="p-2 hover:bg-zinc-800 rounded-full">
                     <ArrowLeft size={20} className="text-zinc-400" />
                 </button>
             )}
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="text-amber-500" />
            {id ? "Edit Quotation" : "Create Quotation"}
            </h1>
        </div>
        <button 
          onClick={handleSave}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-500 text-black px-6 py-2 rounded font-bold flex items-center gap-2 transition"
        >
          <Save size={18} />
          {loading ? "Saving..." : (id ? "Update & Save" : "Save Quotation")}
        </button>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN - CLIENT DETAILS */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-[#161616] p-6 rounded border border-zinc-800">
            <h2 className="text-zinc-400 text-xs uppercase font-bold mb-4 flex items-center gap-2">
              <User size={14} /> Client Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Date</label>
                <input type="date" name="date" value={client.date} onChange={handleClientChange} className="w-full bg-black border border-zinc-700 p-2 rounded text-white focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Client Name</label>
                <input type="text" name="clientName" value={client.clientName} onChange={handleClientChange} placeholder="Enter Name" className="w-full bg-black border border-zinc-700 p-2 rounded text-white focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Phone</label>
                <input type="text" name="clientPhone" value={client.clientPhone} onChange={handleClientChange} placeholder="Phone Number" className="w-full bg-black border border-zinc-700 p-2 rounded text-white focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Site Address</label>
                <textarea name="siteAddress" value={client.siteAddress} onChange={handleClientChange} placeholder="Enter Address" className="w-full bg-black border border-zinc-700 p-2 rounded text-white focus:border-amber-500 outline-none min-h-[80px]" />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - SECTIONS & ITEMS */}
        <div className="lg:col-span-2 space-y-6">
          {sections.map((section, sIdx) => (
            <div key={sIdx} className="bg-[#161616] p-6 rounded border border-zinc-800 relative group">
              <div className="flex justify-between items-start mb-4">
                 <div className="w-full mr-4">
                    <label className="text-zinc-500 text-xs uppercase mb-1 block">Section Name</label>
                    <input 
                      type="text" 
                      className="bg-transparent text-xl font-bold text-amber-500 w-full border-b border-zinc-700 focus:border-amber-500 outline-none pb-1"
                      value={section.sectionName}
                      onChange={(e) => handleSectionNameChange(sIdx, e.target.value)}
                      placeholder="e.g. Master Bedroom"
                    />
                 </div>
                 <button onClick={() => removeSection(sIdx)} className="text-zinc-600 hover:text-red-500 transition"><Trash2 size={18} /></button>
              </div>

              {/* ITEMS LIST */}
              {section.items.map((item, iIdx) => (
                <div key={iIdx} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 items-start bg-zinc-900/50 p-4 rounded border border-zinc-800/50">
                  
                   {/* NEW: DROPDOWN SELECTOR */}
                   <div className="md:col-span-12">
                      <select 
                        className="w-full bg-black border border-zinc-700 text-zinc-300 text-xs p-2 rounded mb-2 focus:border-amber-500 outline-none"
                        onChange={(e) => handleMasterItemSelect(sIdx, iIdx, e.target.value)}
                        defaultValue="custom"
                      >
                        <option value="custom">Select Item from Master Sheet (or type manually below)...</option>
                        {masterItems.map((m, mIdx) => (
                            <option key={mIdx} value={m.description}>
                                {m.category} : {m.description.substring(0, 60)}...
                            </option>
                        ))}
                      </select>
                   </div>

                  <div className="md:col-span-7">
                    <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Description</label>
                    <textarea 
                      className="w-full bg-black border border-zinc-700 p-2 rounded text-white text-sm focus:border-amber-500 outline-none min-h-[60px]"
                      placeholder="Item Details..."
                      value={item.description}
                      onChange={(e) => handleItemChange(sIdx, iIdx, "description", e.target.value)}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Qty (sqft)</label>
                    <input type="number" className="w-full bg-black border border-zinc-700 p-2 rounded text-white text-sm focus:border-amber-500 outline-none"
                      value={item.quantity} onChange={(e) => handleItemChange(sIdx, iIdx, "quantity", Number(e.target.value))} />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Rate</label>
                    <input type="number" className="w-full bg-black border border-zinc-700 p-2 rounded text-white text-sm focus:border-amber-500 outline-none"
                      value={item.rate} onChange={(e) => handleItemChange(sIdx, iIdx, "rate", Number(e.target.value))} />
                  </div>

                  <div className="md:col-span-1 flex justify-center pt-6">
                     <button onClick={() => removeItem(sIdx, iIdx)} className="text-zinc-600 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                  
                  <div className="md:col-span-12 text-right border-t border-zinc-800 pt-2 mt-1">
                     <span className="text-zinc-500 text-xs mr-2">Amount:</span>
                     <span className="text-amber-500 font-bold">₹ {(item.quantity * item.rate).toLocaleString()}</span>
                  </div>
                </div>
              ))}

              <button onClick={() => addItem(sIdx)} className="w-full py-2 border border-dashed border-zinc-700 text-zinc-400 hover:text-amber-500 hover:border-amber-500 rounded flex justify-center items-center gap-2 text-sm transition">
                <Plus size={16} /> Add Item
              </button>
            </div>
          ))}

          <button onClick={addSection} className="w-full py-4 bg-[#161616] border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded flex justify-center items-center gap-2 font-bold transition">
            <Plus size={20} /> Add New Section
          </button>
        </div>

      </div>

      {/* SUMMARY FOOTER */}
      <div className="fixed bottom-0 right-0 w-full bg-[#161616] border-t border-zinc-800 p-6 flex flex-col md:flex-row justify-between items-center z-20 shadow-2xl">
         <div className="text-zinc-500 text-xs hidden md:block">
            * Saving updates database and generates PDF automatically.
         </div>
         
         <div className="flex items-center gap-8">
            <div className="text-right">
               <p className="text-zinc-400 text-xs uppercase">Sub Total</p>
               <p className="text-white font-bold">₹ {calculateTotal().toLocaleString()}</p>
            </div>
            <div className="text-right">
               <p className="text-zinc-400 text-xs uppercase">Adjustment (-)</p>
               <input 
                 type="number" 
                 className="bg-black border border-zinc-700 w-24 text-right px-2 py-1 rounded text-white text-sm"
                 value={adjustment}
                 onChange={(e) => setAdjustment(Number(e.target.value))}
               />
            </div>
            <div className="text-right pl-8 border-l border-zinc-700">
               <p className="text-amber-500 text-xs uppercase font-bold tracking-widest">Grand Total</p>
               <p className="text-2xl font-black text-white">₹ {(calculateTotal() - adjustment).toLocaleString()}</p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default CreateQuotation;